import { Input, Box, Typography, Select, MenuItem, IconButton, Button } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ARIA_LABEL_SUFFIX } from '../../../../../staticComponents/StaticHtmlGenerator';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; // ⭐ CHANGE
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'; // ⭐ CHANGE

interface SharePointDatasetProps {
  setSiteUrl: (url: string) => void;
  setWorkflowUrl: (url: string) => void;
  setSharePointDatasetName: (name: string) => void;
  setIncludeSubfolder: (include: boolean) => void;
  setLibraryFolderPath: (path: string[]) => void;
}

export default function SharePointDataset({
  setSiteUrl,
  setWorkflowUrl,
  setSharePointDatasetName,
  setIncludeSubfolder,
  setLibraryFolderPath,
}: SharePointDatasetProps) {
  const [selectType, setSelectType] = React.useState<string>('site');
  const [includeAll, setIncludeAll] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const [folderPaths, setFolderPaths] = React.useState<string[]>(['']);

  const handleAddPath = () => {
    setFolderPaths([...folderPaths, '']);
  };

  const handleRemovePath = (index: number) => {
    const updated = folderPaths.filter((_, i) => i !== index);
    setFolderPaths(updated);
    setLibraryFolderPath(updated);
  };

  const handlePathChange = (index: number, value: string) => {
    const updated = [...folderPaths];
    updated[index] = value;
    setFolderPaths(updated);
    setLibraryFolderPath(updated);
  };

  // When selectType changes to 'folder', reset includeSubfolder to false
  React.useEffect(() => {
    if (selectType === 'folder') {
      setIncludeSubfolder(false);
      setIncludeAll(true);
    }
  }, [selectType]);

  // When includeAll is checked, set includeSubfolder accordingly
  React.useEffect(() => {
    if (selectType === 'folder') {
      setIncludeSubfolder(includeAll);
    }
  }, [includeAll, selectType]);

  return (
    <Box>
      <Typography fontSize={16} fontWeight={700} mb={1} id="site-folder-select-label">
        {t('fileUpload.selectType')}
      </Typography>
      <Select
        size="small"
        fullWidth
        value={selectType}
        onChange={(e) => setSelectType(e.target.value as string)}
        sx={{ marginBottom: '1rem' }}
        inputProps={{
          'aria-labelledby': 'site-folder-select-label',
        }}
      >
        <MenuItem value="site">{t('fileUpload.selectSite')}</MenuItem>
        <MenuItem value="folder">{t('fileUpload.selectByFolder')}</MenuItem>
      </Select>
      {selectType === 'site' && (
        <>
          <Typography fontSize={16} fontWeight={700} id="document-name-label">
            {t('fileUpload.enterDocumentName')} <span aria-hidden="true">*</span>
          </Typography>
          <Input
            fullWidth
            id="document-name-input"
            sx={{ padding: '0.2rem 0rem', marginBottom: '1rem' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSharePointDatasetName(e.target.value);
            }}
            inputProps={{
              'aria-labelledby': 'document-name-label',
              'aria-required': 'true',
              'aria-label': `${t('fileUpload.enterDocumentName')} ${ARIA_LABEL_SUFFIX.REQUIRED}`,
            }}
          />
          <Typography fontSize={16} fontWeight={700} id="sharepoint-description-label" component="label" htmlFor="site-url">
            {t('fileUpload.enterSiteUrl')}
          </Typography>
          <Input
            fullWidth
            id="site-url"
            sx={{ padding: '0.2rem 0rem' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSiteUrl(e.target.value);
            }}
            inputProps={{
              'aria-labelledby': 'sharepoint-description-label',
              'aria-label': `${t('fileUpload.enterSiteUrl')} ${ARIA_LABEL_SUFFIX.EDIT_OPTIONAL}`,
            }}
          />
          <Typography
            fontSize={16}
            fontWeight={700}
            sx={{ marginTop: '1rem' }}
            id="sharepoint-workflow-label"
            component="label"
            htmlFor="workflow-url"
          >
            {t('fileUpload.enterWorkflowURL')}
          </Typography>
          <Input
            fullWidth
            id="workflow-url"
            sx={{ padding: '0.2rem 0rem' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setWorkflowUrl(e.target.value);
            }}
            inputProps={{
              'aria-labelledby': 'sharepoint-workflow-label',
              'aria-label': `${t('fileUpload.enterWorkflowURL')} ${ARIA_LABEL_SUFFIX.EDIT_OPTIONAL}`,
            }}
          />

        </>
      )}
      {selectType === 'folder' && (
        <Box>
          <Box display="flex" justifyContent={'right'} mb={2}>
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                id="include-all"
                style={{ marginRight: 4 }}
                checked={includeAll}
                onChange={(e) => setIncludeAll(e.target.checked)}
              />
              <label htmlFor="include-all" style={{ fontSize: 14 }}>
                {t('fileUpload.includeAllDocuments')}
              </label>
            </Box>
          </Box>
          {/* includeSubfolder is managed by logic above. You can use it as needed for submission or other logic. */}
          {/* Always show all fields regardless of includeAll checkbox */}
          <>
            <Typography fontSize={16} fontWeight={700} id="folder-path-label">
              {t('fileUpload.enterFolderPath')} <span aria-hidden="true">*</span>
            </Typography>
            {folderPaths.map((path, index) => (
              <Box key={index} display="flex" alignItems="center" mb={1}>
                <Input
                  fullWidth
                  value={path}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handlePathChange(index, e.target.value)
                  }
                  sx={{ padding: '0.2rem 0rem', border: '1px solid #ccc', borderRadius: '6px' }}
                  inputProps={{
                    'aria-labelledby': 'folder-path-label',
                    'aria-required': 'true',
                    'aria-label': `${t('fileUpload.enterFolderPath')} ${ARIA_LABEL_SUFFIX.REQUIRED}`,
                  }}
                />
                {index > 0 && (
                  <IconButton
                    aria-label="Remove folder path"
                    onClick={() => handleRemovePath(index)}
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddPath}
              >
                {t('fileUpload.addFolderPath')}
              </Button>
            </Box>

            <Typography fontSize={16} fontWeight={700} id="document-name-label">
              {t('fileUpload.enterDocumentName')} <span aria-hidden="true">*</span>
            </Typography>
            <Input
              fullWidth
              id="document-name-input"
              sx={{ padding: '0.2rem 0rem', marginBottom: '1rem' }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSharePointDatasetName(e.target.value);
              }}
              inputProps={{
                'aria-labelledby': 'document-name-label',
                'aria-required': 'true',
                'aria-label': `${t('fileUpload.enterDocumentName')} ${ARIA_LABEL_SUFFIX.REQUIRED}`,
              }}
            />
            <Typography fontSize={16} fontWeight={700} id="sharepoint-description-label" component="label" htmlFor="site-url">
              {t('fileUpload.enterSiteUrl')}
            </Typography>
            <Input
              fullWidth
              id="site-url"
              sx={{ padding: '0.2rem 0rem' }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSiteUrl(e.target.value);
              }}
              inputProps={{
                'aria-labelledby': 'sharepoint-description-label',
                'aria-label': `${t('fileUpload.enterSiteUrl')} ${ARIA_LABEL_SUFFIX.EDIT_OPTIONAL}`,
              }}
            />
            <Typography
              fontSize={16}
              fontWeight={700}
              sx={{ marginTop: '1rem' }}
              id="sharepoint-workflow-label"
              component="label"
              htmlFor="workflow-url"
            >
              {t('fileUpload.enterWorkflowURL')}
            </Typography>
            <Input
              fullWidth
              id="workflow-url"
              sx={{ padding: '0.2rem 0rem' }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setWorkflowUrl(e.target.value);
              }}
              inputProps={{
                'aria-labelledby': 'sharepoint-workflow-label',
                'aria-label': `${t('fileUpload.enterWorkflowURL')} ${ARIA_LABEL_SUFFIX.EDIT_OPTIONAL}`,
              }}
            />

          </>
        </Box>
      )}
    </Box>
  );
}
